//#region store import
import { setDocumentation } from "../ReduxStore/translationSlice";
//#endregion

//#region functions import
import { copyObject } from "../Helper/CopyObject";
//#endregion

/**
 * Remove the language and file extension from the translation key.
 * @param {Object} item - The item containing the translation key.
 * @returns {Object} - The item with the modified translation key.
 */
const removeLanguageAndExtension = (item) => {
    const clonedItem = copyObject(item);
    clonedItem.translationKey = clonedItem.translationKey.replace(/_[a-z]{2}(-[a-z]{2})?\.[^.]+$/, '');
    return clonedItem;
};

/**
 * Filter the items based on the provided language.
 * @param {Array} items - The array of items to filter.
 * @param {string} lang - The language code to filter by (e.g., 'fr', 'en').
 */
export const filterByLanguage = (items, lang) => {
    const grouped = {};

    items.forEach(item => {
        const baseKey = item.translationKey.replace(/_[a-z]{2}(-[a-z]{2})?\.[^.]+$/, '');
        grouped[baseKey] = grouped[baseKey] || [];
        grouped[baseKey].push(item);
    });
    const result = [];

    Object.values(grouped).forEach(variants => {
        const preferred = variants.find(i => i.translationKey.includes(`_${lang}.`))
            || variants.find(i => i.translationKey.includes('_en.'))
            || variants.find(i => i.translationKey.includes('_fr.'));
            
        if (preferred) {
            result.push(removeLanguageAndExtension(preferred));
        }
    });

    return result;
};





/**
 * Recursively process the documentation tree to filter by language.
 * @param {Object} node - The current node in the documentation tree.
 * @param {string} language - The language code to filter by (e.g., 'fr', 'en').
 * @returns {Object} - The processed node with filtered translations.
 */
export const processFilterByLanguage = (node, language) => {
    if (Array.isArray(node)) {
        return filterByLanguage(node, language);
    }

    const result = {};
    for (const [key, value] of Object.entries(node)) {
        if (typeof value === 'object') {
            const filtered = processFilterByLanguage(value, language);
            if (Array.isArray(filtered)  && filtered.length > 0) {
                result[key] = filtered;
            } else if (!Array.isArray(filtered) && Object.keys(filtered).length > 0) {
                result[key] = filtered;
            }
        }
    }
    return result;
};

/**
 * Create the documentation object based on the provided tree and language.
 * It filters the documentation tree to include only the relevant machines and translations.
 * @param {Object} documentationTree - The original documentation tree.
 * @param {string} language - The language code to filter translations (e.g., 'fr', 'en').
 * @param {Object} machinesInCenter - Object containing the machines present in the center.
 * @param {Function} dispatch - Redux dispatch function to update the documentation in the store.
 */
export const createDocumentationObject = (documentationTree, language, machinesInCenter, dispatch) => {
    const machineCodes = machinesInCenter.map(machine => machine.code);
    const machinesSoftwareCodes = machinesInCenter.map(machine => machine.softwareCode);

    const filteredTree = copyObject(documentationTree);

    if (filteredTree.Documentation?.SimulationDocumentation) {
        const simDocs = filteredTree.Documentation.SimulationDocumentation;
        
        Object.keys(simDocs).forEach(simulationElement => {
            if (!machineCodes.includes(simulationElement)) {
                if (machinesSoftwareCodes.includes(simulationElement)) {
                    const machinesWithSoftware = machinesInCenter.filter(
                        machine => machine.softwareCode === simulationElement
                    );

                    machinesWithSoftware.forEach(machine => {
                        if (simDocs[machine.code]) {
                            simDocs[machine.code] = {
                                ...simDocs[machine.code],
                                ...simDocs[simulationElement]
                            };
                        } 
                        else if(simDocs[machine.softwareCode] && !simDocs[machine.code]) {
                            simDocs[machine.code] = {
                                "Documentation": [],
                                ...simDocs[simulationElement]
                            };
                        }
                    });
                }
                
                delete simDocs[simulationElement];
            }
        });
    }
    const filteredTreeWithLanguage = processFilterByLanguage(copyObject(filteredTree), language);
    dispatch(setDocumentation(filteredTreeWithLanguage));
};
